/**
 * Email Activity detail drawer.
 *
 * Opens over the Email Activity list (the list keeps its filters and page)
 * and shows, for ONE email Pulse sent — marketing (`mkt_sends`) or
 * transactional (`email_messages`: proposals, CRM, client, system…) — what
 * was actually sent, what the provider has confirmed, and the recipient's
 * current safety state.
 *
 * Truthfulness rules mirrored from the backend
 * (`GET /api/marketing/activity/{send_id}`):
 *  - The body is rendered only when it was retained at send time; otherwise
 *    we say so. Nothing is re-rendered from today's template. Security
 *    emails (password reset, sign-in) are never returned by the API.
 *  - Opens/clicks: "Not tracked" when there is no tracking evidence — never
 *    "No". Scanner/bot activity is listed but never counted as human.
 *  - "Accepted by provider" and "Delivered (provider-confirmed)" are
 *    different badges — a successful API call is not a delivery.
 *
 * Reliability: every load has its own AbortController (aborted on close,
 * unmount, and when another row is opened) and a hard timeout, and always
 * ends in data, an explicit error with Retry, or nothing (closed).
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { X, ArrowsClockwise, Warning, Spinner } from "@phosphor-icons/react";
import api from "@/lib/api";
import { formatLocal } from "@/lib/timezone";

export const DETAIL_TIMEOUT_MS = 20000;
export const BODY_NOT_RETAINED = "Exact sent body was not retained for this historical email.";

const PROVIDER_STATE = {
  QUEUED: { label: "Queued — not yet handed to provider", tone: "amber" },
  ACCEPTED_BY_PROVIDER: { label: "Accepted by provider", tone: "blue" },
  DELIVERED: { label: "Delivered (provider-confirmed)", tone: "green" },
  DEFERRED: { label: "Deferred by receiving server", tone: "amber" },
  BOUNCED: { label: "Bounced", tone: "red" },
  DROPPED: { label: "Dropped by provider", tone: "red" },
  BLOCKED: { label: "Blocked by receiving server", tone: "red" },
  FAILED: { label: "Failed — not sent", tone: "red" },
  SAFE_MODE_BLOCKED: { label: "Blocked by safe mode — not sent", tone: "gray" },
  BLOCKED_SUPPRESSED: { label: "Blocked — address suppressed, not sent", tone: "red" },
  DEMO_SIMULATED: { label: "Simulated (demo) — not sent", tone: "gray" },
  PREVIEW_BLOCKED: { label: "Not sent (preview environment)", tone: "gray" },
};

// Human wording for an opened/clicked state from the API.
export function engagementText(e) {
  if (!e) return "—";
  if (e.state === "not_sent") return "—";
  if (e.state === "not_tracked") return "Not tracked";
  // Eyvo reading its own mail (sales@, staff) — never recipient engagement.
  if (e.state === "internal") {
    const k = e.human_count ?? 0;
    return k ? `Internal${k > 1 ? ` (${k}x)` : ""}` : "No";
  }
  const n = e.human_count ?? e.count ?? 0;
  if (e.state === "no" || !n) return "No";
  return n > 1 ? `${n}x` : "Yes";
}

const TONE = {
  green: "bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]",
  blue: "bg-[#EFF6FF] text-[#1E3A8A] border-[#BFDBFE]",
  amber: "bg-[#FEF9C3] text-[#854D0E] border-[#FDE68A]",
  red: "bg-[#FEE2E2] text-[#991B1B] border-[#FECACA]",
  gray: "bg-[#FAFAFA] text-[#525252] border-[#E5E5E5]",
};

const RECIPIENT_TONE = {
  SUBSCRIBED: "green",
  UNSUBSCRIBED: "red",
  SUPPRESSED: "red",
  DO_NOT_SEND: "red",
  INVALID_ADDRESS: "red",
  SALES_OWNED: "amber",
  REPLIED: "amber",
};

const ELIGIBLE = { YES: "green", NO: "red", UNKNOWN: "gray" };

const fmt = (v) => (v ? formatLocal(v) : "—");

const Badge = ({ tone = "gray", children, testid }) => (
  <span data-testid={testid}
        className={`inline-block px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider border ${TONE[tone]}`}>
    {children}
  </span>
);

const Row = ({ label, children }) => (
  <div className="grid grid-cols-[110px_1fr] gap-2 py-0.5 text-[11px]">
    <div className="text-[#737373] font-semibold">{label}</div>
    <div className="text-[#0A0A0A] break-all">{children ?? "—"}</div>
  </div>
);

const Section = ({ title, children }) => (
  <section className="border-b border-[#F0F0F0] px-4 py-3">
    <h3 className="text-[10px] font-bold uppercase tracking-wider text-[#525252] mb-2">{title}</h3>
    {children}
  </section>
);

function describeError(err, timedOut) {
  if (timedOut) {
    return "The email details took too long to load (over 20 seconds). The list is unaffected — try again.";
  }
  const status = err?.response?.status;
  if (status === 404) return "This email activity record no longer exists.";
  if (status === 403) return "You don't have permission to view this email's details.";
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string" && detail) return `Could not load email details: ${detail}`;
  return "Could not load email details. The list is unaffected — try again.";
}

export default function EmailActivityDetailDrawer({ sendId, onClose }) {
  const [state, setState] = useState({ status: "idle", data: null, error: null });
  const ctrlRef = useRef(null);

  const load = useCallback(() => {
    if (ctrlRef.current) ctrlRef.current.abort();
    if (!sendId) return;
    const ctrl = new AbortController();
    ctrlRef.current = ctrl;
    let timedOut = false;
    const timer = setTimeout(() => { timedOut = true; ctrl.abort(); }, DETAIL_TIMEOUT_MS);
    setState({ status: "loading", data: null, error: null });
    api.get(`/marketing/activity/${encodeURIComponent(sendId)}`, {
      signal: ctrl.signal,
      timeout: DETAIL_TIMEOUT_MS,
    })
      .then((res) => {
        if (ctrlRef.current !== ctrl) return;
        setState({ status: "ready", data: res.data, error: null });
      })
      .catch((err) => {
        // Superseded (another row opened / drawer closed): stay silent.
        if (ctrlRef.current !== ctrl) return;
        if (ctrl.signal.aborted && !timedOut) return;
        setState({ status: "error", data: null, error: describeError(err, timedOut) });
      })
      .finally(() => clearTimeout(timer));
  }, [sendId]);

  useEffect(() => {
    load();
    return () => {
      if (ctrlRef.current) ctrlRef.current.abort();
      ctrlRef.current = null;
    };
  }, [load]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  if (!sendId) return null;
  const d = state.data;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="email-detail-drawer">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} aria-hidden="true" />
      <aside role="dialog" aria-modal="true" aria-label="Email details"
             className="relative h-full w-full max-w-2xl bg-white shadow-xl flex flex-col">
        <header className="flex items-center justify-between px-4 py-3 border-b border-[#E5E5E5]">
          <div className="min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#A3A3A3]">
              Email details{d?.category_label ? ` · ${d.category_label}` : ""}
            </div>
            <div className="text-sm font-bold text-[#0A0A0A] truncate">
              {d?.message?.subject || (state.status === "loading" ? "Loading…" : "—")}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} disabled={state.status === "loading"}
                    className="p-1.5 border border-[#E5E5E5] hover:border-[#0055FF] disabled:opacity-40"
                    aria-label="Refresh email details" data-testid="email-detail-refresh">
              <ArrowsClockwise size={14} />
            </button>
            <button onClick={onClose} className="p-1.5 border border-[#E5E5E5] hover:border-[#0055FF]"
                    aria-label="Close email details" data-testid="email-detail-close">
              <X size={14} />
            </button>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto">
          {state.status === "loading" && (
            <div className="flex items-center gap-2 p-6 text-xs text-[#525252]" data-testid="email-detail-loading">
              <Spinner size={14} className="animate-spin" /> Loading email details…
            </div>
          )}

          {state.status === "error" && (
            <div className="m-4 p-3 border border-[#FECACA] bg-[#FEF2F2] text-xs text-[#991B1B]"
                 role="alert" data-testid="email-detail-error">
              <div className="flex items-start gap-2">
                <Warning size={14} className="mt-0.5 shrink-0" />
                <span>{state.error}</span>
              </div>
              <div className="mt-2 flex gap-2">
                <button onClick={load} className="px-2 py-1 border border-[#991B1B] font-bold"
                        data-testid="email-detail-retry">Retry</button>
                <button onClick={onClose} className="px-2 py-1 border border-[#E5E5E5] text-[#525252]">Close</button>
              </div>
            </div>
          )}

          {state.status === "ready" && d && <DetailBody d={d} />}
        </div>
      </aside>
    </div>
  );
}

function DetailBody({ d }) {
  const m = d.message || {};
  const del = d.delivery || {};
  const rec = d.recipient || {};
  const ctx = d.context || {};
  const ts = d.timestamps || {};
  const ps = PROVIDER_STATE[del.provider_state] || { label: del.provider_state || "Unknown", tone: "gray" };
  const transactional = d.source === "transactional";
  const proposal = ctx.proposal;

  return (
    <>
      {d.warnings?.length > 0 && (
        <div className="mx-4 mt-3 p-2 border border-[#FDE68A] bg-[#FFFBEB] text-[11px] text-[#854D0E]"
             data-testid="email-detail-warnings">
          Some details could not be loaded: {d.warnings.join(" ")}
        </div>
      )}

      <Section title="Message">
        <Row label="From">{m.from_email ? `${m.from_name ? `${m.from_name} ` : ""}<${m.from_email}>` : "Not recorded for this send"}</Row>
        <Row label="To">{m.to}</Row>
        {m.cc && <Row label="CC">{Array.isArray(m.cc) ? m.cc.join(", ") : m.cc}</Row>}
        {m.bcc && <Row label="BCC">{m.bcc.join(", ")}</Row>}
        <Row label="Reply-To">{m.reply_to}</Row>
        <Row label="Subject">{m.subject}</Row>
        <Row label="Sent">{fmt(ts.sent_at)}</Row>
        {m.attachments_count > 0 && <Row label="Attachments">{m.attachments_count}</Row>}
        {transactional && m.bcc_recorded === false && (
          <div className="text-[10px] text-[#A3A3A3]">BCC was not recorded for emails sent before this ledger upgrade.</div>
        )}
        <div className="mt-2">
          {m.body_retained ? (
            m.html ? (
              <iframe title="Email body as sent" sandbox="" srcDoc={m.html}
                      className="w-full h-96 border border-[#E5E5E5] bg-white"
                      data-testid="email-detail-body-html" />
            ) : (
              <pre className="whitespace-pre-wrap text-[11px] p-2 border border-[#E5E5E5] bg-[#FAFAFA]"
                   data-testid="email-detail-body-text">{m.text}</pre>
            )
          ) : m.body_withheld ? (
            <div className="p-2 border border-dashed border-[#D4D4D4] text-[11px] text-[#525252] italic"
                 data-testid="email-detail-body-withheld">
              {m.body_note}
            </div>
          ) : (
            <div className="p-2 border border-dashed border-[#D4D4D4] text-[11px] text-[#525252] italic"
                 data-testid="email-detail-body-missing">
              {BODY_NOT_RETAINED}
            </div>
          )}
          {m.body_retained && m.captured_at && (
            <div className="text-[10px] text-[#A3A3A3] mt-1">Captured at send time {fmt(m.captured_at)}</div>
          )}
        </div>
      </Section>

      <Section title="Delivery">
        <div className="mb-2 flex flex-wrap items-center gap-2">
          <Badge tone={ps.tone} testid="email-detail-provider-state">{ps.label}</Badge>
          {del.provider_state === "ACCEPTED_BY_PROVIDER" && (
            <span className="text-[10px] text-[#737373]">No delivery confirmation received from the provider yet.</span>
          )}
          {del.state && /^(BOUNCED|BLOCKED)/.test(del.state.code || "") && (
            <span className="text-[10px] text-[#525252]" data-testid="email-detail-classification">
              Classified: {del.state.label}
            </span>
          )}
        </div>
        {!transactional && <Row label="Queued">{fmt(ts.queued_at)}</Row>}
        {ts.scheduled_for && <Row label="Scheduled for">{fmt(ts.scheduled_for)}</Row>}
        {transactional && <Row label="Attempted">{fmt(ts.attempted_at)}</Row>}
        <Row label="Accepted">{fmt(ts.sent_at)}</Row>
        <Row label="Delivered">{fmt(ts.delivered_at)}</Row>
        {del.opened ? (
          <>
            <Row label="Opened">
              <span data-testid="email-detail-opened">{engagementText(del.opened)}</span>
              {del.opened.bot_count ? ` (plus ${del.opened.bot_count} from scanners — not counted)` : ""}
            </Row>
            <Row label="Clicked">
              <span data-testid="email-detail-clicked">{engagementText(del.clicked)}</span>
              {del.clicked?.bot_count ? ` (plus ${del.clicked.bot_count} from scanners — not counted)` : ""}
            </Row>
          </>
        ) : (
          <Row label="Opens / clicks">
            {`${del.opens ?? 0} / ${del.clicks ?? 0}`}
            {(del.bot_opens || del.bot_clicks) ? ` (plus ${del.bot_opens || 0} / ${del.bot_clicks || 0} from scanners)` : ""}
          </Row>
        )}
        {proposal?.read_receipt && (
          <Row label="Proposal opens">
            {`${proposal.read_receipt.open_count} (read-receipt pixel)`}
            <div className="text-[10px] text-[#A3A3A3]">{proposal.read_receipt.note}</div>
          </Row>
        )}
        {del.error_message && <Row label="Error"><span className="text-[#991B1B]">{del.error_message}</span></Row>}
        {del.problem_event && (
          <Row label="Provider reason">
            <span className="text-[#991B1B]">
              {[del.problem_event.reason, del.problem_event.response, del.problem_event.smtp_status]
                .filter(Boolean).join(" · ") || del.problem_event.event_type}
            </span>
          </Row>
        )}
        <div className="mt-2">
          <div className="text-[10px] font-semibold text-[#737373] mb-1">Provider event timeline</div>
          {del.timeline?.length ? (
            <ol className="text-[11px] space-y-1" data-testid="email-detail-timeline">
              {del.timeline.map((t, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-[#737373] whitespace-nowrap">{fmt(t.occurred_at)}</span>
                  <span className="font-bold uppercase text-[10px]">{t.provider_event || t.event_type}</span>
                  <span className="text-[#525252] break-all" title={[t.reason, t.response, t.url].filter(Boolean).join(" ")}>
                    {[t.bounce_class, t.reason, t.response, t.url].filter(Boolean).join(" · ")}
                    {(t.event_type === "clicked" || t.event_type === "opened")
                      ? (t.is_bot ? ` — scanner/bot${t.bot_reason ? ` (${t.bot_reason})` : ""}` : " — human")
                      : ""}
                  </span>
                </li>
              ))}
            </ol>
          ) : (
            <div className="text-[11px] text-[#A3A3A3] italic">
              {del.tracking === "not_tracked"
                ? "Not tracked — no provider events were recorded for this email."
                : "No provider events recorded for this send."}
            </div>
          )}
        </div>
      </Section>

      <Section title="Recipient safety (current)">
        <div className="flex flex-wrap gap-1 mb-2" data-testid="email-detail-recipient-states">
          {(rec.current_states || []).map((s) => (
            <Badge key={s.code} tone={RECIPIENT_TONE[s.code] || "gray"}>{s.label}</Badge>
          ))}
          {!(rec.current_states || []).length && <span className="text-[11px] text-[#A3A3A3]">Unknown</span>}
        </div>
        {rec.unsubscribed_at && <Row label="Unsubscribed">{fmt(rec.unsubscribed_at)}</Row>}
        {rec.suppression && <Row label="Suppressed">{`${rec.suppression.reason} — ${fmt(rec.suppression.added_at)}`}</Row>}
        {rec.lead_status && <Row label="Lead status">{rec.lead_status}</Row>}
        <Row label="Eligible when sent">
          <Badge tone={ELIGIBLE[rec.eligible_when_sent] || "gray"} testid="email-detail-eligible">
            {rec.eligible_when_sent || "UNKNOWN"}
          </Badge>
          <div className="text-[10px] text-[#525252] mt-0.5">{rec.eligible_when_sent_reason}</div>
        </Row>
        {rec.later_change_note && (
          <div className="mt-1 text-[10px] text-[#525252] italic">{rec.later_change_note}</div>
        )}
      </Section>

      <Section title="Context">
        <Row label="Type">{d.category_label}</Row>
        {transactional ? (
          <>
            {proposal && (
              <Row label="Proposal">
                <a href={proposal.link} className="text-[#0055FF] underline" data-testid="email-detail-proposal-link">
                  {proposal.quote_number || proposal.quote_id}
                </a>
              </Row>
            )}
            {ctx.related_link && !proposal && (
              <Row label="Related">
                <a href={ctx.related_link} className="text-[#0055FF] underline">{ctx.related_entity_type}</a>
              </Row>
            )}
            {!ctx.related_link && ctx.related_entity_type && (
              <Row label="Related">{`${ctx.related_entity_type} ${ctx.related_entity_id || ""}`}</Row>
            )}
            {ctx.lead_id && <Row label="Lead"><a href={`/crm/leads/${ctx.lead_id}`} className="text-[#0055FF] underline">{ctx.lead_id}</a></Row>}
            {ctx.client_id && <Row label="Client"><a href={`/clients/${ctx.client_id}`} className="text-[#0055FF] underline">{ctx.client_id}</a></Row>}
          </>
        ) : (
          <>
            <Row label="Workflow">{ctx.workflow_name || ctx.workflow_id}</Row>
            <Row label="Step">{ctx.step_label}</Row>
            <Row label="Campaign">{ctx.campaign_name || ctx.campaign_id || ctx.source_marker}</Row>
            {ctx.lead_id && <Row label="Lead"><a href={`/crm/leads/${ctx.lead_id}`} className="text-[#0055FF] underline">{ctx.lead_id}</a></Row>}
          </>
        )}
        <Row label="Contact">{[ctx.contact_name, ctx.company_name].filter(Boolean).join(" · ") || null}</Row>
        <Row label={transactional ? "Message ID" : "Send ID"}><code>{ctx.send_id}</code></Row>
        <Row label="Provider ID"><code>{ctx.provider_message_id || "—"}</code></Row>
        {ctx.enrollment_id && <Row label="Enrolment"><code>{ctx.enrollment_id}</code></Row>}
        {d.is_test_send && <Row label="Test send">Yes</Row>}
      </Section>
    </>
  );
}
